import type { ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Download, Plus, Search, Trash2 } from 'lucide-react'
import { Boton, BotonIcono } from '@/ui/Boton'
import { InsigniaDiscreta, InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, FechaCorta, FechaHora, Monto, RNC } from '@/ui/Numero'
import { IndicadorDGII } from '@/app/layouts/IndicadorDGII'
import { PRESENTACION_ESTADO } from '@/dominio/documentos/estados'
import type { EstadoDocumento } from '@/dominio/documentos/tipos'
import { Etiqueta, Rejilla, Seccion, Tarjeta } from './-componentes'

export const Route = createFileRoute('/sistema/')({
  component: Fundamentos,
})

const COLORES_MARCA = [
  { nombre: 'marca', valor: '#0d1b33', uso: 'Botón primario, enlace, elemento activo' },
  { nombre: 'marca-fuerte', valor: '#0a0a0a', uso: 'Estado sobre el primario' },
  { nombre: 'marca-tenue', valor: '#e6eaf2', uso: 'Fondo de fila activa y de selección' },
]

const GRISES = [
  { nombre: 'gris-900', valor: '#16181d', uso: 'Texto principal' },
  { nombre: 'gris-700', valor: '#3a3f47', uso: 'Texto de énfasis secundario' },
  { nombre: 'gris-600', valor: '#5a616b', uso: 'Texto secundario y etiquetas' },
  { nombre: 'gris-400', valor: '#a7adb6', uso: 'Deshabilitado y marcador' },
  { nombre: 'gris-300', valor: '#d7dbe0', uso: 'Bordes y separadores, 1px' },
  { nombre: 'gris-200', valor: '#e6e9ed', uso: 'Separador de fila' },
  { nombre: 'gris-100', valor: '#f0f2f5', uso: 'Fondo de control en reposo' },
  { nombre: 'gris-50', valor: '#f7f8fa', uso: 'Fondo de app y encabezado de tabla' },
]

const ESTADOS = [
  { nombre: 'verde', valor: '#0f7a43', uso: 'Aceptado' },
  { nombre: 'ambar', valor: '#9a5b00', uso: 'En proceso, aceptado condicional' },
  { nombre: 'rojo', valor: '#b3261e', uso: 'Rechazado, error' },
  { nombre: 'azul', valor: '#1b5fbf', uso: 'Información neutral' },
]

const ESPACIOS = [
  { clase: 'p-1', valor: 4 },
  { clase: 'p-2', valor: 8 },
  { clase: 'p-3', valor: 12 },
  { clase: 'p-4', valor: 16 },
  { clase: 'p-6', valor: 24 },
  { clase: 'p-8', valor: 32 },
  { clase: 'p-12', valor: 48 },
]

const TODOS_LOS_ESTADOS: EstadoDocumento[] = [
  'borrador',
  'encolado',
  'en_contingencia',
  'en_proceso',
  'aceptado',
  'aceptado_condicional',
  'rechazado',
  'anulado',
]

function Fundamentos() {
  return (
    <div className="flex flex-col gap-8">
      {/* Color ---------------------------------------------------------- */}
      <Seccion
        titulo="Color"
        nota="Un solo color de marca, usado con avaricia. El resto es gris y los colores de estado. Todos los colores de estado pasan 4.5:1 sobre blanco como texto."
      >
        <Rejilla columnas={3}>
          <Tarjeta>
            <Etiqueta>Marca</Etiqueta>
            <MuestrasColor colores={COLORES_MARCA} />
          </Tarjeta>
          <Tarjeta>
            <Etiqueta>Grises</Etiqueta>
            <MuestrasColor colores={GRISES} />
          </Tarjeta>
          <Tarjeta>
            <Etiqueta>Estado</Etiqueta>
            <MuestrasColor colores={ESTADOS} />
          </Tarjeta>
        </Rejilla>
      </Seccion>

      {/* Tipografía ----------------------------------------------------- */}
      <Seccion
        titulo="Tipografía"
        nota="Una familia de interfaz y una monoespaciada para números. Todo número va en monoespaciada y alineado a la derecha: es la señal más rápida de que quien construyó esto entiende software financiero."
      >
        <Tarjeta className="flex flex-col gap-3">
          <FilaTipografia etiqueta="Título de pantalla · 20px / 600">
            <span className="text-pantalla text-gris-900">Aprobaciones pendientes</span>
          </FilaTipografia>
          <FilaTipografia etiqueta="Título de sección · 15px / 600">
            <span className="text-seccion text-gris-900">Corte 15 oct · facturas de septiembre</span>
          </FilaTipografia>
          <FilaTipografia etiqueta="Cuerpo y tabla · 14px / 400">
            <span className="text-cuerpo text-gris-900">Suplidora del Este</span>
          </FilaTipografia>
          <FilaTipografia etiqueta="Etiqueta y ayuda · 12px / 400">
            <span className="text-etiqueta text-gris-600">Verificado contra DGII · existe</span>
          </FilaTipografia>
          <FilaTipografia etiqueta="Monoespaciada · 13px / 400">
            <span className="mono text-gris-900">E310000000847</span>
          </FilaTipografia>
        </Tarjeta>

        <Tarjeta>
          <Etiqueta>Los números se alinean</Etiqueta>
          <table className="mt-2 w-full max-w-[420px] border-collapse">
            <caption className="solo-lectores">Ejemplo de alineación de montos</caption>
            <tbody>
              {[1247890.5, 284500, 67200.25, 12340, 980.4].map((valor) => (
                <tr key={valor} className="h-[var(--alto-fila-normal)] border-b border-gris-200">
                  <td className="text-cuerpo text-gris-700">Suplidora del Este</td>
                  <td className="text-right">
                    <Monto valor={valor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tarjeta>
      </Seccion>

      {/* Espaciado ------------------------------------------------------ */}
      <Seccion
        titulo="Espaciado, radio y alturas"
        nota="Escala de 4px: 4, 8, 12, 16, 24, 32, 48. Nada fuera de esa escala. Un solo radio de 6px en todo el producto. Sombra solo en lo que flota."
      >
        <Rejilla columnas={2}>
          <Tarjeta>
            <Etiqueta>Escala de 4px</Etiqueta>
            <ul className="mt-2 flex flex-col gap-1">
              {ESPACIOS.map((espacio) => (
                <li key={espacio.clase} className="flex items-center gap-3">
                  <span className="mono w-16 text-gris-600">{espacio.clase}</span>
                  <span
                    aria-hidden="true"
                    className="block h-2 bg-marca"
                    style={{ width: `${espacio.valor}px` }}
                  />
                  <span className="numero w-12 text-gris-600">{espacio.valor}px</span>
                </li>
              ))}
            </ul>
          </Tarjeta>

          <Tarjeta>
            <Etiqueta>Alturas</Etiqueta>
            <ul className="mt-2 flex flex-col gap-2">
              <AlturaMuestra etiqueta="Fila cómoda" variable="--alto-fila-comoda" valor="44px" />
              <AlturaMuestra etiqueta="Fila normal · campo · botón" variable="--alto-control" valor="36px" />
              <AlturaMuestra etiqueta="Fila compacta" variable="--alto-fila-compacta" valor="28px" />
              <AlturaMuestra etiqueta="Barra superior" variable="--alto-barra-superior" valor="56px" />
            </ul>
          </Tarjeta>
        </Rejilla>
      </Seccion>

      {/* Botones -------------------------------------------------------- */}
      <Seccion
        titulo="Botones"
        nota="36px de alto, radio de 6px, borde de 1px. El rojo solo en lo que destruye. El foco es un anillo de 2px, siempre visible: recorre esta sección con el tabulador."
      >
        <Tarjeta className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Boton variante="primario" icono={<Plus />}>
              Nueva factura
            </Boton>
            <Boton variante="primario" atajo="Ctrl+⏎">
              Emitir
            </Boton>
            <Boton variante="secundario" icono={<Download />}>
              Descargar CSV
            </Boton>
            <Boton variante="discreto">Cancelar</Boton>
            <Boton variante="destructivo" icono={<Trash2 />}>
              Anular rango
            </Boton>
            <Boton variante="primario" disabled>
              Deshabilitado
            </Boton>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Boton variante="secundario" tamano="menor">
              Tamaño menor
            </Boton>
            <BotonIcono etiqueta="Buscar" icono={<Search />} />
            <BotonIcono etiqueta="Buscar" icono={<Search />} tamano="menor" />
          </div>
        </Tarjeta>
      </Seccion>

      {/* Insignias ------------------------------------------------------ */}
      <Seccion
        titulo="Insignia de estado"
        nota="Punto de color más texto, nunca solo el punto y nunca fondo completo de fila. Aceptado condicional va en ámbar con la palabra Válido al lado: es válido y el usuario no debe alarmarse."
      >
        <Tarjeta>
          <ul className="flex flex-col gap-2">
            {TODOS_LOS_ESTADOS.map((estado) => {
              const presentacion = PRESENTACION_ESTADO[estado]
              return (
                <li key={estado} className="flex items-center gap-4">
                  <span className="w-56">
                    <InsigniaEstado
                      tono={presentacion.tono}
                      aclaracion={presentacion.aclaracion}
                      titulo={presentacion.descripcion}
                    >
                      {presentacion.etiqueta}
                    </InsigniaEstado>
                  </span>
                  <span className="text-etiqueta text-gris-600">{presentacion.descripcion}</span>
                </li>
              )
            })}
          </ul>

          <div className="mt-4 flex items-center gap-2">
            <InsigniaDiscreta>migrado</InsigniaDiscreta>
            <InsigniaDiscreta>borrador</InsigniaDiscreta>
            <InsigniaDiscreta>reconstruida</InsigniaDiscreta>
            <span className="text-etiqueta text-gris-600">
              Insignia discreta: informa el origen, no alarma.
            </span>
          </div>
        </Tarjeta>
      </Seccion>

      {/* Números -------------------------------------------------------- */}
      <Seccion
        titulo="Números"
        nota="Monto, RNC, e-NCF y fecha pasan siempre por los mismos formateadores de lib/. Un monto formateado distinto en dos pantallas es lo que hace que un producto se sienta improvisado."
      >
        <Tarjeta>
          <table className="w-full max-w-[720px] border-collapse">
            <thead>
              <tr className="border-b border-gris-300">
                <th scope="col" className="h-[var(--alto-fila-normal)] text-etiqueta text-gris-600">
                  Componente
                </th>
                <th scope="col" className="h-[var(--alto-fila-normal)] text-etiqueta text-gris-600">
                  Resultado
                </th>
                <th scope="col" className="h-[var(--alto-fila-normal)] text-etiqueta text-gris-600">
                  Nota
                </th>
              </tr>
            </thead>
            <tbody>
              <FilaNumero componente="<Monto>" nota="Separador de miles y dos decimales">
                <Monto valor={29736} />
              </FilaNumero>
              <FilaNumero componente="<Monto mostrarMoneda>" nota="El símbolo separado del número">
                <Monto valor={1247890.5} mostrarMoneda />
              </FilaNumero>
              <FilaNumero componente="<Monto>" nota="El negativo de una nota de crédito se ve">
                <Monto valor={-1200} />
              </FilaNumero>
              <FilaNumero componente="<RNC>" nota="Con guiones al mostrar, sin guiones al enviar">
                <RNC valor="131880738" />
              </FilaNumero>
              <FilaNumero componente="<RNC>" nota="Cédula de 11 dígitos">
                <RNC valor="00100000009" />
              </FilaNumero>
              <FilaNumero componente="<ENCF>" nota="Copiable con un clic: pasa el ratón por encima">
                <ENCF valor="E310000000847" />
              </FilaNumero>
              <FilaNumero componente="<ENCF abreviado>" nota="Para columnas estrechas">
                <ENCF valor="E310000000847" abreviado />
              </FilaNumero>
              <FilaNumero componente="<FechaCorta>" nota="dd/MM/yyyy">
                <FechaCorta valor="2026-09-21" />
              </FilaNumero>
              <FilaNumero componente="<FechaHora>" nota="En hora de República Dominicana">
                <FechaHora valor="2026-09-21T18:32:00Z" />
              </FilaNumero>
            </tbody>
          </table>
        </Tarjeta>
      </Seccion>

      {/* Indicador de DGII ---------------------------------------------- */}
      <Seccion
        titulo="Indicador de DGII"
        nota="Siempre visible en la barra superior. El cuarto estado es el que se olvida: si el navegador no alcanza nuestro servidor, el estado de DGII es desconocido y se dice. Un verde rancio durante una caída es peor que no tener indicador."
      >
        <Tarjeta className="flex flex-col items-start gap-1">
          <IndicadorDGII estado="normal" />
          <IndicadorDGII estado="lenta" />
          <IndicadorDGII estado="caida" pendientes={12} horasDelMasViejo={9} diasEnContingencia={2} />
          <IndicadorDGII estado="sin-dato" />
        </Tarjeta>
      </Seccion>
    </div>
  )
}

/* ---------------------------------------------------------------------- */

function MuestrasColor({ colores }: { colores: { nombre: string; valor: string; uso: string }[] }) {
  return (
    <ul className="mt-2 flex flex-col gap-1">
      {colores.map((color) => (
        <li key={color.nombre} className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="size-6 shrink-0 rounded-md border border-gris-300"
            style={{ backgroundColor: color.valor }}
          />
          <span className="min-w-0 flex-1">
            <span className="mono block text-gris-900">{color.nombre}</span>
            <span className="block truncate text-etiqueta text-gris-600">{color.uso}</span>
          </span>
          <span className="mono text-gris-600">{color.valor}</span>
        </li>
      ))}
    </ul>
  )
}

function FilaTipografia({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-4">
      <span className="w-64 shrink-0 text-etiqueta text-gris-600">{etiqueta}</span>
      {children}
    </div>
  )
}

function AlturaMuestra({
  etiqueta,
  variable,
  valor,
}: {
  etiqueta: string
  variable: string
  valor: string
}) {
  return (
    <li className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="block w-24 rounded-md border border-gris-300 bg-gris-100"
        style={{ height: `var(${variable})` }}
      />
      <span className="flex-1 text-cuerpo text-gris-700">{etiqueta}</span>
      <span className="numero text-gris-600">{valor}</span>
    </li>
  )
}

function FilaNumero({
  componente,
  nota,
  children,
}: {
  componente: string
  nota: string
  children: ReactNode
}) {
  return (
    <tr className="h-[var(--alto-fila-normal)] border-b border-gris-200">
      <td className="mono text-gris-600">{componente}</td>
      <td className="text-right">{children}</td>
      <td className="pl-6 text-etiqueta text-gris-600">{nota}</td>
    </tr>
  )
}
